import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Box, Typography, Button, IconButton, Switch, Chip, Alert, Divider, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Select, MenuItem, FormControl, InputLabel, Autocomplete, ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import { Page, Card } from '../ui';

const METRICS = [
  { v: 'spend', l: 'Расход' }, { v: 'impressions', l: 'Показы' }, { v: 'clicks', l: 'Клики' },
  { v: 'ctr', l: 'CTR' }, { v: 'cpc', l: 'CPC' }, { v: 'cpm', l: 'CPM' },
  { v: 'leads', l: 'Лиды' }, { v: 'cpl', l: 'CPL' }, { v: 'daily_budget', l: 'Дневной бюджет' },
];
const OPS = [{ v: '>', l: 'больше' }, { v: '<', l: 'меньше' }, { v: '>=', l: '≥' }, { v: '<=', l: '≤' }, { v: '==', l: '=' }, { v: '!=', l: '≠' }];
const ACTIONS = [{ v: 'pause', l: 'Пауза' }, { v: 'activate', l: 'Включить' }, { v: 'budget', l: 'Изменить бюджет' }, { v: 'notify', l: 'Уведомить в Telegram' }];
const BUDGET_MODES = [{ v: 'set', l: 'Установить' }, { v: 'increase', l: 'Увеличить на' }, { v: 'decrease', l: 'Уменьшить на' }];
const WINDOWS = [
  { v: 'today', l: 'Сегодня' }, { v: 'yesterday', l: 'Вчера' }, { v: 'last_3d', l: '3 дня' },
  { v: 'last_7d', l: '7 дней' }, { v: 'last_14d', l: '14 дней' }, { v: 'last_30d', l: '30 дней' },
];
const DAYS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];   // индекс 0..6 = weekday (Пн=0)

const mLabel = (v) => (METRICS.find((m) => m.v === v) || {}).l || v;
const aLabel = (v) => (ACTIONS.find((a) => a.v === v) || {}).l || v;
const emptyRule = () => ({
  name: '', conditions: [{ metric: 'spend', op: '>', value: '' }], logic: 'AND',
  actions: [{ type: 'notify' }], scope: 'campaign', enabled: false, dry_run: true,
  window: 'today', min_spend: 0, min_events: 0, cooldown_minutes: 0, schedule: null, account_ids: [],
});

// шаблоны-рецепты (метрики только те, что реально считает движок: spend/ctr/cpc/cpm/leads/cpl)
const TEMPLATES = [
  { name: 'Стоп убыткам', conditions: [{ metric: 'spend', op: '>', value: '50' }], actions: [{ type: 'pause' }], window: 'today', min_events: 3 },
  { name: 'Высокий CPA (уведомить)', conditions: [{ metric: 'cpl', op: '>', value: '30' }], actions: [{ type: 'notify' }], window: 'last_3d', min_spend: 30 },
  { name: 'Низкий CTR', conditions: [{ metric: 'ctr', op: '<', value: '0.5' }], actions: [{ type: 'pause' }], window: 'last_3d', min_spend: 5 },
  { name: 'Страж бюджета', conditions: [{ metric: 'spend', op: '>', value: '100' }], actions: [{ type: 'notify' }], window: 'today' },
  { name: 'Без результатов', conditions: [{ metric: 'leads', op: '<=', value: '0' }], actions: [{ type: 'pause' }], window: 'last_3d', min_spend: 20 },
  { name: 'Дешёвые лиды → буст', conditions: [{ metric: 'cpl', op: '<', value: '10' }, { metric: 'leads', op: '>=', value: '5' }], logic: 'AND', actions: [{ type: 'budget', mode: 'increase', value: '20', percent: true }], window: 'last_7d', cooldown_minutes: 360 },
];
const mkTemplate = (tpl) => ({ ...emptyRule(), ...tpl });

function RuleEditor({ open, initial, onClose, onSaved }) {
  const [r, setR] = useState(emptyRule());
  const [saving, setSaving] = useState(false);
  const [accounts, setAccounts] = useState([]);
  useEffect(() => { if (open) setR(initial ? JSON.parse(JSON.stringify(initial)) : emptyRule()); }, [open, initial]);
  useEffect(() => { if (open) axios.get('/accounts').then((x) => setAccounts(x.data)).catch(() => {}); }, [open]);

  const setField = (k, v) => setR((p) => ({ ...p, [k]: v }));
  // расписание
  const schedOn = !!r.schedule;
  const setSchedOn = (on) => setField('schedule', on ? { days: [0, 1, 2, 3, 4], from: '09:00', to: '18:00' } : null);
  const toggleDay = (d) => setR((p) => {
    const cur = (p.schedule && p.schedule.days) || [];
    const days = cur.includes(d) ? cur.filter((x) => x !== d) : [...cur, d].sort((a, b) => a - b);
    return { ...p, schedule: { ...p.schedule, days } };
  });
  const setSchedField = (k, v) => setR((p) => ({ ...p, schedule: { ...p.schedule, [k]: v } }));
  const setCond = (i, k, v) => setR((p) => { const c = [...p.conditions]; c[i] = { ...c[i], [k]: v }; return { ...p, conditions: c }; });
  const addCond = () => setR((p) => ({ ...p, conditions: [...p.conditions, { metric: 'spend', op: '>', value: '' }] }));
  const delCond = (i) => setR((p) => ({ ...p, conditions: p.conditions.filter((_, j) => j !== i) }));
  const setAct = (i, k, v) => setR((p) => { const a = [...p.actions]; a[i] = { ...a[i], [k]: v }; return { ...p, actions: a }; });
  const addAct = () => setR((p) => ({ ...p, actions: [...p.actions, { type: 'notify' }] }));
  const delAct = (i) => setR((p) => ({ ...p, actions: p.actions.filter((_, j) => j !== i) }));

  const save = async () => {
    setSaving(true);
    try {
      if (r.id) await axios.put(`/rules/${r.id}`, r);
      else await axios.post('/rules', r);
      onSaved();
      onClose();
    } catch (e) { /* ignore */ }
    setSaving(false);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
      <DialogTitle sx={{ fontWeight: 700 }}>{r.id ? 'Изменить правило' : 'Новое правило'}</DialogTitle>
      <DialogContent dividers>
        <TextField label="Название" fullWidth size="small" value={r.name} onChange={(e) => setField('name', e.target.value)} sx={{ mb: 2.5 }} />

        <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 1 }}>Условия</Typography>
        {r.conditions.map((c, i) => (
          <Box key={i} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
            <FormControl size="small" sx={{ minWidth: 130 }}>
              <Select value={c.metric} onChange={(e) => setCond(i, 'metric', e.target.value)}>
                {METRICS.map((m) => <MenuItem key={m.v} value={m.v}>{m.l}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 90 }}>
              <Select value={c.op} onChange={(e) => setCond(i, 'op', e.target.value)}>
                {OPS.map((o) => <MenuItem key={o.v} value={o.v}>{o.l}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField size="small" type="number" placeholder="значение" value={c.value}
              onChange={(e) => setCond(i, 'value', e.target.value)} sx={{ width: 120 }} />
            <IconButton size="small" onClick={() => delCond(i)} disabled={r.conditions.length <= 1}><DeleteOutlineRoundedIcon fontSize="small" /></IconButton>
          </Box>
        ))}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2.5 }}>
          <Button size="small" startIcon={<AddRoundedIcon />} onClick={addCond}>Условие</Button>
          {r.conditions.length > 1 && (
            <FormControl size="small" sx={{ minWidth: 110 }}>
              <InputLabel>Логика</InputLabel>
              <Select label="Логика" value={r.logic} onChange={(e) => setField('logic', e.target.value)}>
                <MenuItem value="AND">И (все)</MenuItem>
                <MenuItem value="OR">ИЛИ (любое)</MenuItem>
              </Select>
            </FormControl>
          )}
        </Box>

        <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 1 }}>Действия</Typography>
        {r.actions.map((a, i) => (
          <Box key={i} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <Select value={a.type} onChange={(e) => setAct(i, 'type', e.target.value)}>
                {ACTIONS.map((o) => <MenuItem key={o.v} value={o.v}>{o.l}</MenuItem>)}
              </Select>
            </FormControl>
            {a.type === 'budget' && (
              <>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <Select value={a.mode || 'set'} onChange={(e) => setAct(i, 'mode', e.target.value)}>
                    {BUDGET_MODES.map((o) => <MenuItem key={o.v} value={o.v}>{o.l}</MenuItem>)}
                  </Select>
                </FormControl>
                <TextField size="small" type="number" placeholder={a.percent && a.mode !== 'set' ? '%' : '$'} value={a.value || ''} onChange={(e) => setAct(i, 'value', e.target.value)} sx={{ width: 90 }} />
                {(a.mode === 'increase' || a.mode === 'decrease') && (
                  <ToggleButtonGroup size="small" exclusive value={a.percent ? 'percent' : 'amount'} onChange={(e, v) => v && setAct(i, 'percent', v === 'percent')}>
                    <ToggleButton value="percent" sx={{ px: 1.25 }}>%</ToggleButton>
                    <ToggleButton value="amount" sx={{ px: 1.25 }}>$</ToggleButton>
                  </ToggleButtonGroup>
                )}
              </>
            )}
            <IconButton size="small" onClick={() => delAct(i)} disabled={r.actions.length <= 1}><DeleteOutlineRoundedIcon fontSize="small" /></IconButton>
          </Box>
        ))}
        <Button size="small" startIcon={<AddRoundedIcon />} onClick={addAct} sx={{ mb: 2 }}>Действие</Button>

        <Divider sx={{ my: 1.5 }} />
        <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 1 }}>Окно и гарды</Typography>
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 1.5 }}>
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel>Окно метрик</InputLabel>
            <Select label="Окно метрик" value={r.window} onChange={(e) => setField('window', e.target.value)}>
              {WINDOWS.map((w) => <MenuItem key={w.v} value={w.v}>{w.l}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField size="small" type="number" label="Мин. расход $" value={r.min_spend} onChange={(e) => setField('min_spend', Number(e.target.value) || 0)} sx={{ width: 130 }} />
          <TextField size="small" type="number" label="Мин. событий" value={r.min_events} onChange={(e) => setField('min_events', Number(e.target.value) || 0)} sx={{ width: 130 }} />
          <TextField size="small" type="number" label="Cooldown, мин" value={r.cooldown_minutes} onChange={(e) => setField('cooldown_minutes', Number(e.target.value) || 0)} sx={{ width: 140 }} />
        </Box>
        {r.actions.some((a) => a.type === 'budget' && a.mode !== 'set') && !r.cooldown_minutes ? (
          <Alert severity="warning" sx={{ mb: 1.5, borderRadius: '6px', fontSize: 12 }}>
            Для правил с изменением бюджета задай Cooldown (хотя бы 60 мин) — иначе бюджет вырастет кратно за счёт повторных срабатываний.
          </Alert>
        ) : null}

        <Autocomplete
          multiple size="small" options={accounts} getOptionLabel={(o) => o.name || o.id}
          value={accounts.filter((a) => (r.account_ids || []).includes(a.id))}
          onChange={(e, v) => setField('account_ids', v.map((x) => x.id))}
          isOptionEqualToValue={(o, v) => o.id === v.id}
          renderInput={(params) => <TextField {...params} label="Аккаунты (пусто = все)" placeholder={(r.account_ids || []).length ? '' : 'все профили'} />}
          sx={{ mb: 1.5 }}
        />

        <Box sx={{ display: 'flex', alignItems: 'center', mb: schedOn ? 1 : 0 }}>
          <Switch checked={schedOn} onChange={(e) => setSchedOn(e.target.checked)} />
          <Typography sx={{ fontSize: 13 }}>Расписание (иначе 24/7). Время — UTC</Typography>
        </Box>
        {schedOn && (
          <Box sx={{ pl: 1, mb: 1 }}>
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
              {DAYS.map((d, i) => (
                <Chip key={i} label={d} size="small" variant={(r.schedule.days || []).includes(i) ? 'filled' : 'outlined'}
                  color={(r.schedule.days || []).includes(i) ? 'primary' : 'default'} onClick={() => toggleDay(i)} sx={{ cursor: 'pointer' }} />
              ))}
            </Box>
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <TextField size="small" type="time" label="С" InputLabelProps={{ shrink: true }} value={r.schedule.from || ''} onChange={(e) => setSchedField('from', e.target.value)} sx={{ width: 120 }} />
              <TextField size="small" type="time" label="До" InputLabelProps={{ shrink: true }} value={r.schedule.to || ''} onChange={(e) => setSchedField('to', e.target.value)} sx={{ width: 120 }} />
            </Box>
          </Box>
        )}

        <Divider sx={{ my: 1.5 }} />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Switch checked={r.dry_run} onChange={(e) => setField('dry_run', e.target.checked)} />
            <Typography sx={{ fontSize: 13 }}>Dry Run (только журнал)</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Switch checked={r.enabled} onChange={(e) => setField('enabled', e.target.checked)} />
            <Typography sx={{ fontSize: 13 }}>Включено</Typography>
          </Box>
        </Box>
        {!r.dry_run && (
          <Alert severity="warning" sx={{ mt: 1.5, borderRadius: '6px' }}>
            Dry Run выключен — действия будут выполняться реально в Facebook.
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose}>Отмена</Button>
        <Button variant="contained" onClick={save} disabled={saving || !r.name.trim()}>
          {saving ? 'Сохранение…' : 'Сохранить'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function RulesPage() {
  const [rules, setRules] = useState([]);
  const [logs, setLogs] = useState([]);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [preview, setPreview] = useState(null);
  const [toast, setToast] = useState(null);
  const [tplOpen, setTplOpen] = useState(false);
  const applyTemplate = (tpl) => { setTplOpen(false); setEditing(mkTemplate(tpl)); setEditorOpen(true); };

  const load = useCallback(() => {
    axios.get('/rules').then((r) => setRules(r.data)).catch(() => {});
    axios.get('/rules/logs').then((r) => setLogs(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = async (rule, field) => {
    try { await axios.put(`/rules/${rule.id}`, { ...rule, [field]: !rule[field] }); load(); } catch (e) { /* ignore */ }
  };
  const remove = async (id) => { try { await axios.delete(`/rules/${id}`); load(); } catch (e) { /* ignore */ } };
  const runRule = async (id) => {
    setToast(null);
    try { const r = await axios.post(`/rules/${id}/run`); setToast({ t: 'success', x: `Запущено. Совпадений: ${r.data.matched}` }); load(); }
    catch (e) { setToast({ t: 'error', x: 'Ошибка запуска' }); }
  };
  const doPreview = async (id) => {
    try { const r = await axios.get(`/rules/${id}/preview`); setPreview(r.data); } catch (e) { setPreview({ count: 0, matched: [], error: true }); }
  };

  return (
    <Page
      title="Правила"
      subtitle="Автоматизация: условия → действия. По умолчанию — безопасный режим Dry Run."
      action={(
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" onClick={() => setTplOpen(true)}>Шаблоны</Button>
          <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={() => { setEditing(null); setEditorOpen(true); }}>Новое правило</Button>
        </Box>
      )}
    >
      {toast && <Alert severity={toast.t} sx={{ mb: 2, borderRadius: '6px' }} onClose={() => setToast(null)}>{toast.x}</Alert>}

      {rules.length === 0 ? (
        <Card sx={{ p: 5, textAlign: 'center' }}>
          <Typography sx={{ fontSize: 14, fontWeight: 600, color: 'text.primary' }}>Правил пока нет</Typography>
          <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mt: 0.5 }}>Нажми «Новое правило», чтобы создать первое.</Typography>
        </Card>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {rules.map((rule) => (
            <Card key={rule.id} sx={{ p: 2.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
                <Box sx={{ flex: 1, minWidth: 220 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                    <Typography sx={{ fontSize: 15, fontWeight: 700, color: 'text.primary' }}>{rule.name}</Typography>
                    {rule.dry_run && <Chip size="small" label="Dry Run" color="warning" variant="outlined" sx={{ height: 20, fontSize: 10.5, fontWeight: 700 }} />}
                  </Box>
                  <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>
                    Если {rule.conditions.map((c) => `${mLabel(c.metric)} ${c.op} ${c.value}`).join(rule.logic === 'AND' ? ' и ' : ' или ')}
                    {' → '}
                    {rule.actions.map((a) => aLabel(a.type)).join(', ')}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
                    <Switch size="small" checked={rule.enabled} onChange={() => toggle(rule, 'enabled')} />
                    <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{rule.enabled ? 'Вкл' : 'Выкл'}</Typography>
                  </Box>
                  <IconButton size="small" title="Превью" onClick={() => doPreview(rule.id)}><VisibilityRoundedIcon fontSize="small" /></IconButton>
                  <IconButton size="small" title="Запустить" onClick={() => runRule(rule.id)}><PlayArrowRoundedIcon fontSize="small" /></IconButton>
                  <IconButton size="small" title="Изменить" onClick={() => { setEditing(rule); setEditorOpen(true); }}><EditRoundedIcon fontSize="small" /></IconButton>
                  <IconButton size="small" title="Удалить" onClick={() => remove(rule.id)} sx={{ '&:hover': { color: 'error.main' } }}><DeleteOutlineRoundedIcon fontSize="small" /></IconButton>
                </Box>
              </Box>
            </Card>
          ))}
        </Box>
      )}

      {/* Журнал */}
      <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em', mt: 4, mb: 1.5 }}>
        Журнал срабатываний
      </Typography>
      <Card sx={{ p: 0, overflow: 'hidden' }}>
        {logs.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary', fontSize: 13 }}>Пока пусто. Запусти правило или дождись расписания.</Box>
        ) : (
          <Box>
            {logs.map((l, i) => (
              <Box key={l.id || i} sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 2.5, py: 1.25, borderBottom: i < logs.length - 1 ? '1px solid' : 'none', borderColor: 'divider' }}>
                <Typography sx={{ fontSize: 11.5, color: 'text.secondary', width: 130, flexShrink: 0 }}>
                  {l.evaluated_at ? new Date(l.evaluated_at).toLocaleString('ru-RU') : ''}
                </Typography>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'text.primary', width: 160, flexShrink: 0 }}>{l.entity_name || '—'}</Typography>
                <Chip size="small" label={l.action || '—'} variant="outlined" sx={{ height: 22, fontSize: 11 }} />
                <Typography sx={{ fontSize: 12, color: 'text.secondary', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.detail}</Typography>
              </Box>
            ))}
          </Box>
        )}
      </Card>

      <RuleEditor open={editorOpen} initial={editing} onClose={() => setEditorOpen(false)} onSaved={load} />

      <Dialog open={!!preview} onClose={() => setPreview(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>Превью: на кого сработает</DialogTitle>
        <DialogContent dividers>
          {preview && preview.count === 0 && (
            <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
              Сейчас под условия ничего не подходит (или нет активных кампаний с данными).
            </Typography>
          )}
          {preview && preview.matched && preview.matched.map((e, i) => (
            <Box key={i} sx={{ py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{e.name}</Typography>
              <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>spend ${e.spend} · clicks {e.clicks} · ctr {e.ctr} · leads {e.leads}</Typography>
            </Box>
          ))}
        </DialogContent>
        <DialogActions><Button onClick={() => setPreview(null)}>Закрыть</Button></DialogActions>
      </Dialog>

      <Dialog open={tplOpen} onClose={() => setTplOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>Шаблоны правил</DialogTitle>
        <DialogContent dividers>
          <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mb: 1.5 }}>
            Готовый пресет — отправная точка. Открой, поправь пороги под свои кампании и сохрани. Все создаются в Dry Run.
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {TEMPLATES.map((tpl, i) => (
              <Box key={i} onClick={() => applyTemplate(tpl)}
                sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: '10px', cursor: 'pointer', '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' } }}>
                <Typography sx={{ fontSize: 14, fontWeight: 700 }}>{tpl.name}</Typography>
                <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 0.25 }}>
                  Если {tpl.conditions.map((c) => `${mLabel(c.metric)} ${c.op} ${c.value}`).join(tpl.logic === 'OR' ? ' или ' : ' и ')}
                  {' → '}{tpl.actions.map((a) => aLabel(a.type)).join(', ')}
                  {' · окно '}{(WINDOWS.find((w) => w.v === (tpl.window || 'today')) || {}).l}
                </Typography>
              </Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions><Button onClick={() => setTplOpen(false)}>Закрыть</Button></DialogActions>
      </Dialog>
    </Page>
  );
}
